import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import { close, downloadOutline, shareOutline } from 'ionicons/icons';
import './MapaAstral.css';
import AstroChart from './AstroChart';
import AspectList from './AspectList';
import { CreateAssociatedNatalChartDto, NatalChartForecastStatus, NatalChartResultDto, Planets, PlatformFeature, UserNatalChartsResponseDto, UserTreatmentPreference } from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { useSubscription } from '../SubscriptionProvider';
import { degToDMSString, signPositions } from '../helpers/angle-utils';
import { Loader2, MapPin, Moon, Plus, Sparkles, Star, Sun, Trash2, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BirthDateInput } from './ui/birth-date-input';
import { TimeInput } from './ui/time-input';
import { natalChartToUI } from './astro/adapters/natalChartToAstroChart';
import { getElementVisual } from '../helpers/zodiac-element';
import { type BirthPlaceSuggestion, searchBirthPlaceSuggestions } from '../features/birthPlaceSearch';
import { subscribeToBirthRecordUpdated } from '../features/birthRecordRefresh';
import { buildUtcDatePreservingClock } from '../helpers/time';
import { type BirthDateDraft, EMPTY_BIRTH_DATE_DRAFT, maxBirthDate, parseBirthDate, parseBirthTime } from '../helpers/birthDateTime';
import { emitFunnelEvent } from '../platform/funnel';

const PlanetPositions = lazy(() => import('./PlanetPositions'));

type UserNatalChartListItem = {
  id: string;
  name: string;
  birthDateTime?: Date;
  timeZone?: string;
  isPrimary: boolean;
};

type UserNatalChartsSummary = {
  maxAssociatedCharts: number;
  associatedChartsCount: number;
  charts: UserNatalChartListItem[];
};

type CreateAssociatedChartForm = {
  name: string;
  treatmentPreference: UserTreatmentPreference;
  birthDate: BirthDateDraft;
  birthTime: string;
  birthPlace: string;
  birthLatitude?: number;
  birthLongitude?: number;
};

const EMPTY_CREATE_ASSOCIATED_CHART_FORM: CreateAssociatedChartForm = {
  name: '',
  treatmentPreference: UserTreatmentPreference.Masculine,
  birthDate: EMPTY_BIRTH_DATE_DRAFT,
  birthTime: '',
  birthPlace: '',
  birthLatitude: undefined,
  birthLongitude: undefined,
};

// RM-014: background forecast polling cadence and hard time budget.
const FORECAST_POLL_INTERVAL_MS = 5000;
const FORECAST_POLL_TIMEOUT_MS = 120000;

const getSignBaseDegree = (sign: keyof typeof signPositions | undefined) =>
  sign ? signPositions[sign] : 0;

const mapUserNatalChartsSummary = (data: UserNatalChartsResponseDto | null | undefined): UserNatalChartsSummary => {
  const charts: NonNullable<UserNatalChartsResponseDto['charts']> = Array.isArray(data?.charts) ? (data?.charts ?? []) : [];

  return {
    maxAssociatedCharts: data?.maxAssociatedCharts ?? 5,
    associatedChartsCount: data?.associatedChartsCount ?? 0,
    charts: charts.map((chart) => ({
      id: chart?.id ?? '',
      name: chart?.name ?? '',
      birthDateTime: chart?.birthDateTime ?? undefined,
      timeZone: chart?.timeZone ?? 'UTC',
      isPrimary: !!chart?.isPrimary,
    })),
  };
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  if (error && typeof error === 'object') {
    const errorDetails = error as { detail?: unknown; title?: unknown; message?: unknown };

    if (typeof errorDetails.detail === 'string' && errorDetails.detail.trim()) {
      return errorDetails.detail;
    }

    if (typeof errorDetails.title === 'string' && errorDetails.title.trim()) {
      return errorDetails.title;
    }

    if (typeof errorDetails.message === 'string' && errorDetails.message.trim()) {
      return errorDetails.message;
    }
  }

  return fallback;
};

const MapaAstralLoading: React.FC<{ label: string }> = ({ label }) => (
  <div className="space-y-4 md:space-y-6" aria-busy="true" aria-live="polite">
    <div className="space-y-3">
      <div className="mapa-loading-shimmer h-9 w-56 max-w-[82%] rounded-full bg-action-100/80" />
      <div className="mapa-loading-shimmer h-4 w-72 max-w-full rounded-full bg-stone-200/75" />
    </div>

    <Card className="overflow-hidden border-action-100 bg-gradient-to-br from-white via-action-50/60 to-amber-50/70 shadow-sm">
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="mapa-loading-shimmer h-4 w-4 rounded-full bg-action-200/80" />
              <div className="mapa-loading-shimmer h-3 w-32 rounded-full bg-action-200/70" />
            </div>
            <div className="mapa-loading-shimmer h-6 w-48 max-w-full rounded-full bg-stone-200/80" />
            <div className="mapa-loading-shimmer h-4 w-64 max-w-full rounded-full bg-stone-200/70" />
          </div>
          <div className="mapa-loading-shimmer h-6 w-28 rounded-full bg-white/80" />
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
          <div className="space-y-2">
            <div className="mapa-loading-shimmer h-3 w-24 rounded-full bg-stone-200/70" />
            <div className="mapa-loading-shimmer h-10 w-full rounded-lg bg-white/85" />
          </div>
          <div className="flex items-end">
            <div className="mapa-loading-shimmer h-10 w-36 rounded-lg bg-white/80" />
          </div>
          <div className="flex items-end">
            <div className="mapa-loading-shimmer h-10 w-32 rounded-lg bg-white/80" />
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2">
      <Card className="overflow-hidden border-stone-200 bg-white shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="mapa-loading-shimmer h-5 w-5 rounded-full bg-yellow-200/80" />
            <div className="mapa-loading-shimmer h-5 w-40 rounded-full bg-stone-200/80" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="mapa-chart-container">
            <div className="mapa-loading-mandala" aria-hidden="true">
              <div className="mapa-loading-ring mapa-loading-ring--outer" />
              <div className="mapa-loading-ring mapa-loading-ring--middle" />
              <div className="mapa-loading-ring mapa-loading-ring--inner" />
              <div className="mapa-loading-core" />
              <div className="mapa-loading-planet mapa-loading-planet--a" />
              <div className="mapa-loading-planet mapa-loading-planet--b" />
              <div className="mapa-loading-planet mapa-loading-planet--c" />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-slate-500">
            <span className="h-2.5 w-2.5 rounded-full bg-action-500 animate-pulse" />
            {label}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 md:space-y-4">
        {[
          'from-amber-50 to-orange-50 border-amber-200',
          'from-slate-50 to-gray-50 border-slate-200',
          'from-action-50 to-white border-action-200 shadow-sm',
        ].map((gradientClass, index) => (
          <Card key={gradientClass} className={`overflow-hidden bg-gradient-to-br ${gradientClass}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="mapa-loading-shimmer h-5 w-5 rounded-full bg-white/85" />
                <div className="mapa-loading-shimmer h-5 w-40 rounded-full bg-white/85" />
              </div>
              <div className="mt-3">
                <div className="mapa-loading-shimmer h-3 w-24 rounded-full bg-white/75" />
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="mapa-loading-shimmer h-3 w-full rounded-full bg-white/80" />
              <div className="mapa-loading-shimmer h-3 w-5/6 rounded-full bg-white/75" />
              {index === 2 && (
                <div className="mapa-loading-shimmer mt-4 h-16 w-full rounded-2xl bg-white/80" />
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

const MapaAstral: React.FC = () => {
  const { t } = useTranslation();
  const [shareBlob, setShareBlob] = useState<Blob | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showShare, setShowShare] = useState(false);
  const [availableCharts, setAvailableCharts] = useState<UserNatalChartListItem[]>([]);
  const [selectedChartId, setSelectedChartId] = useState('');
  const [maxAssociatedCharts, setMaxAssociatedCharts] = useState(5);
  const [associatedChartsCount, setAssociatedChartsCount] = useState(0);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartDetail, setChartDetail] = useState<NatalChartResultDto | null>(null);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateAssociatedChartForm>(EMPTY_CREATE_ASSOCIATED_CHART_FORM);
  const [showCreateBirthErrors, setShowCreateBirthErrors] = useState(false);
  const [birthPlaceQuery, setBirthPlaceQuery] = useState('');
  const [birthPlaceSuggestions, setBirthPlaceSuggestions] = useState<BirthPlaceSuggestion[]>([]);
  const [birthPlaceError, setBirthPlaceError] = useState<string | null>(null);
  const [isSearchingBirthPlace, setIsSearchingBirthPlace] = useState(false);
  const [isSavingChart, setIsSavingChart] = useState(false);
  const [isDeletingChart, setIsDeletingChart] = useState(false);
  const [forecastPollTimedOut, setForecastPollTimedOut] = useState(false);
  const api = useAstroApiClient();
  const { hasFeature } = useSubscription();
  const history = useHistory();

  const selectedChart = availableCharts.find(chart => chart.id === selectedChartId) ?? null;
  const mappedNatalChart = useMemo(() => (chartDetail ? natalChartToUI(chartDetail) : null), [chartDetail]);
  const chartData = mappedNatalChart?.chartData ?? null;
  const aspects = mappedNatalChart?.aspects ?? [];
  // The primary (own) natal chart's AI reading is included for everyone; associated
  // charts keep the AI narrative behind the NatalReading (Pro) entitlement.
  const canViewNarrativeForecasts = (selectedChart?.isPrimary ?? false) || hasFeature(PlatformFeature.NatalReading);
  // Associated (linked) charts are a Pro-only capability. Basic users (no NatalReading)
  // only see their primary natal chart and a Pro upsell instead of chart management.
  const canUseAssociatedCharts = hasFeature(PlatformFeature.NatalReading);
  // RM-014: the chart arrives immediately; the AI reading may still be generating.
  const forecastStatus = chartDetail?.forecastStatus ?? NatalChartForecastStatus.Ready;
  const forecastPending =
    canViewNarrativeForecasts && forecastStatus === NatalChartForecastStatus.Pending && !forecastPollTimedOut;
  const forecastFailed =
    canViewNarrativeForecasts &&
    (forecastStatus === NatalChartForecastStatus.Failed || forecastPollTimedOut);
  const isAtChartLimit = associatedChartsCount >= maxAssociatedCharts;
  // The endpoint only requires a birth instant; the single boundary is the present,
  // the same one the free tool and the registration apply.
  const today = maxBirthDate();
  const createBirthDate = parseBirthDate(createForm.birthDate, { required: true, max: today });
  const createBirthTime = parseBirthTime(createForm.birthTime, true);
  const canCreateChart =
    createForm.name.trim().length > 0 &&
    typeof createForm.birthLatitude === 'number' &&
    typeof createForm.birthLongitude === 'number';

  const resetCreateForm = useCallback(() => {
    setCreateForm(EMPTY_CREATE_ASSOCIATED_CHART_FORM);
    setShowCreateBirthErrors(false);
    setBirthPlaceQuery('');
    setBirthPlaceSuggestions([]);
    setBirthPlaceError(null);
  }, []);

  const loadSelectedChart = useCallback(async (natalChartId: string) => {
    setChartLoading(true);
    setChartError(null);
    setChartDetail(null);
    setForecastPollTimedOut(false);

    try {
      const detail = await api.apiAstrologyNatalChartsById(natalChartId);
      setChartDetail(detail);
    } catch (error) {
      setChartError(getErrorMessage(error, t('mapaAstralChartLoadError')));
    } finally {
      setChartLoading(false);
    }
  }, [api, t]);

  const loadUserCharts = useCallback(async (preferredChartId?: string) => {
    setChartsLoading(true);
    setChartsError(null);

    try {
      const summary = mapUserNatalChartsSummary(await api.apiAstrologyNatalChartsGet());
      // Basic users cannot manage or view associated charts, so only expose the primary chart.
      const visibleCharts = canUseAssociatedCharts
        ? summary.charts
        : summary.charts.filter(chart => chart.isPrimary);
      setAvailableCharts(visibleCharts);
      setMaxAssociatedCharts(summary.maxAssociatedCharts);
      setAssociatedChartsCount(summary.associatedChartsCount);
      setSelectedChartId(current => {
        const nextId =
          [preferredChartId, current, visibleCharts.find(chart => chart.isPrimary)?.id, visibleCharts[0]?.id]
            .find(candidate => candidate && visibleCharts.some(chart => chart.id === candidate)) ?? '';
        return nextId;
      });
    } catch (error) {
      setAvailableCharts([]);
      setSelectedChartId('');
      setChartsError(getErrorMessage(error, t('mapaAstralChartsLoadError')));
    } finally {
      setChartsLoading(false);
    }
  }, [api, t, canUseAssociatedCharts]);

  useEffect(() => {
    void loadUserCharts();
  }, [loadUserCharts]);

  useEffect(() => {
    return subscribeToBirthRecordUpdated(() => {
      void loadUserCharts(selectedChartId || undefined);
    });
  }, [loadUserCharts, selectedChartId]);

  useEffect(() => {
    if (!selectedChartId) {
      setChartDetail(null);
      setChartError(null);
      setChartLoading(false);
      return;
    }

    void loadSelectedChart(selectedChartId);
  }, [loadSelectedChart, selectedChartId]);

  // GR-002: the natal chart is on screen. The funnel module keeps this to the
  // first rendered chart on the device, which is the step of the funnel.
  useEffect(() => {
    if (!mappedNatalChart) {
      return;
    }

    emitFunnelEvent('chartViewed');
  }, [mappedNatalChart]);

  // RM-014: while the AI reading is generating in background, poll the same
  // endpoint until it becomes Ready/Failed, with a hard time budget.
  useEffect(() => {
    if (!selectedChartId || !forecastPending) {
      return;
    }

    let cancelled = false;
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (Date.now() - startedAt >= FORECAST_POLL_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        if (!cancelled) {
          setForecastPollTimedOut(true);
        }
        return;
      }

      api.apiAstrologyNatalChartsById(selectedChartId)
        .then(detail => {
          if (!cancelled && detail.forecastStatus !== NatalChartForecastStatus.Pending) {
            setChartDetail(detail);
          }
        })
        .catch(() => {
          // Transient poll failures are ignored; the time budget bounds retries.
        });
    }, FORECAST_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [api, selectedChartId, forecastPending]);

  useEffect(() => {
    if (!isCreateDialogOpen) {
      setBirthPlaceSuggestions([]);
      setBirthPlaceError(null);
      setIsSearchingBirthPlace(false);
      return;
    }

    const query = birthPlaceQuery.trim();
    if (query.length < 3) {
      setBirthPlaceSuggestions([]);
      setBirthPlaceError(null);
      setIsSearchingBirthPlace(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsSearchingBirthPlace(true);
      setBirthPlaceError(null);

      searchBirthPlaceSuggestions(api, query)
        .then(results => {
          if (!cancelled) {
            setBirthPlaceSuggestions(results);
          }
        })
        .catch(error => {
          if (!cancelled) {
            setBirthPlaceSuggestions([]);
            setBirthPlaceError(getErrorMessage(error, t('mapaAstralLocationSearchError')));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsSearchingBirthPlace(false);
          }
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [api, birthPlaceQuery, isCreateDialogOpen, t]);

  const handleDownload = () => {
    if (!shareUrl) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = shareUrl;
    anchor.download = 'card.png';
    anchor.click();
  };

  const handleNativeShare = async () => {
    if (!shareBlob || !navigator.share) {
      return;
    }

    const file = new File([shareBlob], 'card.png', { type: 'image/png' });
    try {
      await navigator.share({ files: [file], title: t('mapaAstralShareTitle') });
    } catch (error) {
      console.error(error);
    }
  };

  const closeShare = () => {
    if (shareUrl) {
      URL.revokeObjectURL(shareUrl);
    }

    setShowShare(false);
    setShareUrl(null);
    setShareBlob(null);
  };

  const handleBirthPlaceSelect = (suggestion: BirthPlaceSuggestion) => {
    setCreateForm(current => ({
      ...current,
      birthPlace: suggestion.displayName,
      birthLatitude: suggestion.latitude,
      birthLongitude: suggestion.longitude,
    }));
    setBirthPlaceQuery('');
    setBirthPlaceSuggestions([]);
    setBirthPlaceError(null);
  };

  const handleCreateDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (!open) {
      resetCreateForm();
      setActionError(null);
    }
  };

  const handleOpenCreateDialog = () => {
    if (!canUseAssociatedCharts) {
      return;
    }

    resetCreateForm();
    setActionError(null);
    setIsCreateDialogOpen(true);
  };

  const handleCreateAssociatedChart = async () => {
    if (!canUseAssociatedCharts || !canCreateChart || isSavingChart) {
      return;
    }

    const { birthLatitude, birthLongitude } = createForm;
    if (birthLatitude === undefined || birthLongitude === undefined) {
      return;
    }

    if (createBirthDate.status !== 'valid' || createBirthTime.status !== 'valid') {
      setShowCreateBirthErrors(true);
      return;
    }

    // The civil clock is preserved as typed; the birth time zone stays with the
    // existing geolocation services.
    const birthDateTime = buildUtcDatePreservingClock(createBirthDate.value, createBirthTime.value);
    if (!birthDateTime) {
      return;
    }

    setIsSavingChart(true);
    setActionError(null);

    try {
      const createdChart = await api.apiAstrologyNatalChartsPost(new CreateAssociatedNatalChartDto({
        name: createForm.name.trim(),
        birthDateTime,
        birthLatitude,
        birthLongitude,
        treatmentPreference: createForm.treatmentPreference,
      }));

      handleCreateDialogOpenChange(false);
      await loadUserCharts(createdChart.id ?? undefined);
    } catch (error) {
      setActionError(getErrorMessage(error, t('mapaAstralCreateError')));
    } finally {
      setIsSavingChart(false);
    }
  };

  const handleDeleteAssociatedChart = async () => {
    if (!selectedChart || selectedChart.isPrimary || isDeletingChart) {
      return;
    }

    setIsDeletingChart(true);
    setActionError(null);

    try {
      await api.apiAstrologyNatalChartsDelete(selectedChart.id);
      setIsDeleteDialogOpen(false);
      await loadUserCharts();
    } catch (error) {
      setActionError(getErrorMessage(error, t('mapaAstralDeleteError')));
    } finally {
      setIsDeletingChart(false);
    }
  };

  const openProSubscription = useCallback(() => {
    history.push('/subscription/pro', { returnTo: history.location.pathname });
  }, [history]);

  const renderAiUpsell = (compact = false) => (
    <button
      type="button"
      onClick={openProSubscription}
      aria-label={t('mapaAstralAiUpsellAction')}
      className={`w-full rounded-lg border border-action-200 bg-white/85 text-left transition hover:border-action-300 hover:bg-action-50/80 focus:outline-none focus:ring-2 focus:ring-action-300 ${compact ? 'p-3' : 'p-4'}`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-action-800">
        <Sparkles className="h-4 w-4" />
        <span>{t('mapaAstralAiUpsellTitle')}</span>
        <Badge variant="secondary" className="bg-action-100 text-action-800">
          Pro
        </Badge>
      </div>
      <p className={`${compact ? 'mt-1 text-xs' : 'mt-2 text-sm'} text-slate-600`}>
        {t(compact ? 'mapaAstralAiUpsellInline' : 'mapaAstralAiUpsellDescription')}
      </p>
    </button>
  );

  const renderAssociatedChartsUpsell = () => (
    <button
      type="button"
      onClick={openProSubscription}
      aria-label={t('mapaAstralAssociatedChartsProAction')}
      className="w-full rounded-lg border border-action-200 bg-white/85 p-4 text-left transition hover:border-action-300 hover:bg-action-50/80 focus:outline-none focus:ring-2 focus:ring-action-300"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-action-800">
        <Users className="h-4 w-4" />
        <span>{t('mapaAstralAssociatedChartsProTitle')}</span>
        <Badge variant="secondary" className="bg-action-100 text-action-800">
          Pro
        </Badge>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        {t('mapaAstralAssociatedChartsProDescription')}
      </p>
    </button>
  );

  const renderNarrativeForecast = (forecast: string | undefined, className: string, compact = false) => {
    if (canViewNarrativeForecasts && forecast) {
      return <p className={className}>{forecast}</p>;
    }

    if (forecastPending) {
      return (
        <p className={`${className} animate-pulse`} aria-live="polite">
          {t('mapaAstralForecastGenerating')}
        </p>
      );
    }

    if (canViewNarrativeForecasts) {
      return <p className={className}>{t('noReading')}</p>;
    }

    return renderAiUpsell(compact);
  };

  // RM-014: single banner (instead of one message per forecast slot) when the
  // background generation failed or the polling budget was exceeded.
  const renderForecastFailedBanner = () => {
    if (!forecastFailed || !selectedChartId) {
      return null;
    }

    return (
      <div
        role="alert"
        className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="text-sm text-amber-800">{t('mapaAstralForecastFailed')}</p>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
          onClick={() => void loadSelectedChart(selectedChartId)}
        >
          {t('mapaAstralForecastRetry')}
        </Button>
      </div>
    );
  };

  if (chartsLoading || (!!selectedChartId && chartLoading)) {
    return <MapaAstralLoading label={t('loading')} />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">{t('completeNatalChart')}</h2>
          <p className="mt-1 text-sm md:text-base text-slate-600">
            {selectedChart ? t('mapaAstralSelectedChartDescription', { name: selectedChart.name }) : t('personalizedAnalysis')}
          </p>
        </div>
      </div>

      {renderForecastFailedBanner()}

      <Card className="overflow-hidden border-action-100 bg-gradient-to-br from-white via-action-50/60 to-amber-50/70 shadow-sm">
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-action-500" />
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-action-700">
                  {t('mapaAstralChartSelectorLabel')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-xl font-semibold text-slate-800">
                  {selectedChart?.name ?? t('mapaAstralNoChartsTitle')}
                </h3>
                {selectedChart && (
                  <Badge
                    variant="secondary"
                    className={selectedChart.isPrimary ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'}
                  >
                    {selectedChart.isPrimary ? t('mapaAstralPrimaryChartBadge') : t('mapaAstralAssociatedChartBadge')}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-600">
                {selectedChart
                  ? selectedChart.isPrimary
                    ? t('mapaAstralPrimaryChartDescription')
                    : t('mapaAstralAssociatedChartDescription')
                  : t('mapaAstralNoChartsDescription')}
              </p>
            </div>

            {canUseAssociatedCharts && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-white/80 text-slate-700">
                  {t('mapaAstralAssociatedChartsLimit', { count: associatedChartsCount, max: maxAssociatedCharts })}
                </Badge>
                {!selectedChart?.isPrimary && selectedChart && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                    {t('mapaAstralAssociatedChartAiOnly')}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {canUseAssociatedCharts ? (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
              <div className="space-y-2">
                <Label htmlFor="mapa-astral-chart-selector">{t('mapaAstralChartSelectorLabel')}</Label>
                <Select
                  value={selectedChartId}
                  onValueChange={(value) => {
                    setSelectedChartId(value);
                    setActionError(null);
                  }}
                  disabled={availableCharts.length === 0}
                >
                  <SelectTrigger id="mapa-astral-chart-selector" className="bg-white">
                    <SelectValue placeholder={t('mapaAstralSelectChartPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {availableCharts.map(chart => (
                      <SelectItem key={chart.id} value={chart.id}>
                        {chart.name} · {chart.isPrimary ? t('mapaAstralPrimaryChartBadge') : t('mapaAstralAssociatedChartBadge')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button onClick={handleOpenCreateDialog} disabled={isAtChartLimit}>
                  <Plus className="h-4 w-4" />
                  {t('mapaAstralCreateChart')}
                </Button>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  className="bg-white"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={!selectedChart || selectedChart.isPrimary || isDeletingChart}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('mapaAstralDeleteChart')}
                </Button>
              </div>
            </div>
          ) : (
            renderAssociatedChartsUpsell()
          )}

          {chartsError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {chartsError}
            </div>
          )}

          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {actionError}
            </div>
          )}
        </CardContent>
      </Card>

      {!chartData ? (
        <Card className="border-stone-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-800">{t('mapaAstralNoChartsTitle')}</CardTitle>
            <CardDescription className="text-slate-600">
              {chartError ?? t('mapaAstralNoChartsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button variant="outline" className="bg-white" onClick={() => void loadUserCharts(selectedChartId || undefined)}>
              {t('mapaAstralRetry')}
            </Button>
            {canUseAssociatedCharts && !isAtChartLimit && (
              <Button onClick={handleOpenCreateDialog}>
                <Plus className="h-4 w-4" />
                {t('mapaAstralCreateChart')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2">
            <Card className="border-stone-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-base text-slate-800 md:text-lg">
                  <Star className="mr-2 h-4 w-4 text-yellow-400 md:h-5 md:w-5" />
                  {t('astrologicalMandala')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mapa-chart-container">
                  <AstroChart
                    planets={[
                      { name: Planets.Sun, sign: chartData.sun.sign, degree: chartData.sun.degree, house: chartData.sun.house },
                      { name: Planets.Moon, sign: chartData.moon.sign, degree: chartData.moon.degree, house: chartData.moon.house },
                      ...chartData.planets,
                    ]}
                    ascendant={chartData.ascendant}
                    descendant={chartData.descendant}
                    imumCoeli={chartData.imumCoeli}
                    mediumCoeli={chartData.mediumCoeli}
                    aspects={aspects}
                    houses={chartData.houses}
                    diameter={250}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3 md:space-y-4">
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center text-lg text-slate-800">
                    <Sun className="mr-2 h-5 w-5 text-yellow-400" />
                    {t('planetInSign', { planet: t('planet_Sun'), sign: t('sign_' + chartData.sun.sign) })}
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    {t('house_' + chartData.sun.house)} • {degToDMSString(chartData.sun.degree - signPositions[chartData.sun.sign!])}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {renderNarrativeForecast(chartData.sun.forecast, 'text-sm text-amber-800', true)}
                </CardContent>
              </Card>

              {(() => {
                const elementVisual = getElementVisual(chartData.sun.sign);
                if (!elementVisual) {
                  return null;
                }
                const ElementIcon = elementVisual.icon;
                return (
                  <Card className={elementVisual.cardClass}>
                    <CardHeader className="pb-3">
                      <CardTitle className={`flex items-center text-lg text-slate-800`}>
                        <ElementIcon className={`mr-2 h-5 w-5 ${elementVisual.iconClass}`} />
                        {t('mapaAstralElementTitle', { element: t(elementVisual.labelKey) })}
                      </CardTitle>
                      <CardDescription className={elementVisual.keywordClass}>
                        {t('mapaAstralElementDescription', { sign: t('sign_' + chartData.sun.sign) })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/70 shadow-inner ${elementVisual.iconClass}`}>
                          <ElementIcon className="h-6 w-6" />
                        </span>
                        <div>
                          <p className={`text-xl font-bold ${elementVisual.valueClass}`}>{t(elementVisual.labelKey)}</p>
                          <p className={`text-sm ${elementVisual.keywordClass}`}>{t(elementVisual.keywordKey)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center text-lg text-slate-800">
                    <Moon className="mr-2 h-5 w-5 text-purple-400" />
                    {t('planetInSign', { planet: t('planet_Moon'), sign: t('sign_' + chartData.moon.sign) })}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {t('house_' + chartData.moon.house)} • {degToDMSString(chartData.moon.degree - signPositions[chartData.moon.sign!])}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {renderNarrativeForecast(chartData.moon.forecast, 'text-sm text-slate-600', true)}
                </CardContent>
              </Card>

              <Card className="border-action-200 bg-gradient-to-br from-action-50 to-white shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center text-base text-slate-800 md:text-lg">
                    <Sparkles className="mr-2 h-5 w-5 text-action-500 md:h-6 md:w-6" />
                    {t('personalizedInterpretation')}
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-600 md:text-sm">
                    {t('personalizedInterpretationDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {canViewNarrativeForecasts && chartData.globalForecast ? (
                    <div className="rounded-lg border border-stone-200 bg-white/80 p-4">
                      <p className="text-sm text-slate-600 md:text-base">{chartData.globalForecast}</p>
                    </div>
                  ) : forecastPending ? (
                    <p className="animate-pulse text-sm text-slate-600 md:text-base" aria-live="polite">
                      {t('mapaAstralForecastGenerating')}
                    </p>
                  ) : canViewNarrativeForecasts ? (
                    <p className="text-sm text-slate-600 md:text-base">{t('noReading')}</p>
                  ) : (
                    renderAiUpsell()
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <Card className="border-stone-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-base text-slate-800 md:text-lg">
                {t('angularPoints')}
              </CardTitle>
              <CardDescription className="text-xs text-slate-600 md:text-sm">
                {t('angularPointsDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-slate-800">{t('ascendant')} (AC)</h4>
                    <Badge variant="secondary" className="bg-cyan-200 text-cyan-800">
                      {degToDMSString(chartData.ascendant.degree - getSignBaseDegree(chartData.ascendant.sign))}
                    </Badge>
                  </div>
                  <p className="mb-2 text-sm font-medium text-slate-700">{t('sign_' + chartData.ascendant.sign)}</p>
                  {renderNarrativeForecast(chartData.ascendant.forecast, 'text-xs text-slate-600 md:text-sm', true)}
                </div>

                <div className="rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-slate-800">{t('descendant')} (DC)</h4>
                    <Badge variant="secondary" className="bg-amber-200 text-amber-800">
                      {degToDMSString(chartData.descendant.degree - getSignBaseDegree(chartData.descendant.sign))}
                    </Badge>
                  </div>
                  <p className="mb-2 text-sm font-medium text-slate-700">{t('sign_' + chartData.descendant.sign)}</p>
                  {renderNarrativeForecast(chartData.descendant.forecast, 'text-xs text-slate-600 md:text-sm', true)}
                </div>

                <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-slate-800">{t('imumCoeli')} (IC)</h4>
                    <Badge variant="secondary" className="bg-emerald-200 text-emerald-800">
                      {degToDMSString(chartData.imumCoeli.degree - getSignBaseDegree(chartData.imumCoeli.sign))}
                    </Badge>
                  </div>
                  <p className="mb-2 text-sm font-medium text-slate-700">{t('sign_' + chartData.imumCoeli.sign)}</p>
                  {renderNarrativeForecast(chartData.imumCoeli.forecast, 'text-xs text-slate-600 md:text-sm', true)}
                </div>

                <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-slate-800">{t('mediumCoeli')} (MC)</h4>
                    <Badge variant="secondary" className="bg-purple-200 text-purple-800">
                      {degToDMSString(chartData.mediumCoeli.degree - getSignBaseDegree(chartData.mediumCoeli.sign))}
                    </Badge>
                  </div>
                  <p className="mb-2 text-sm font-medium text-slate-700">{t('sign_' + chartData.mediumCoeli.sign)}</p>
                  {renderNarrativeForecast(chartData.mediumCoeli.forecast, 'text-xs text-slate-600 md:text-sm', true)}
                </div>
              </div>
            </CardContent>
          </Card>

          <PlanetPositions
            planets={chartData.planets}
            showForecasts={canViewNarrativeForecasts}
            forecastPending={forecastPending}
            onUpgradeClick={openProSubscription}
          />

          <AspectList aspects={aspects} />
        </>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={handleCreateDialogOpenChange}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('mapaAstralCreateDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('mapaAstralCreateDialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {isAtChartLimit && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {t('mapaAstralAssociatedChartsLimit', { count: associatedChartsCount, max: maxAssociatedCharts })}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="associated-chart-name">{t('mapaAstralFriendNameLabel')}</Label>
              <Input
                id="associated-chart-name"
                placeholder={t('mapaAstralFriendNamePlaceholder')}
                value={createForm.name}
                onChange={(event) => {
                  const value = event.target.value;
                  setCreateForm(current => ({ ...current, name: value }));
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="associated-chart-treatment-preference">{t('treatmentPreference')}</Label>
              <Select
                value={createForm.treatmentPreference}
                onValueChange={(value: UserTreatmentPreference) => {
                  setCreateForm(current => ({ ...current, treatmentPreference: value }));
                }}
              >
                <SelectTrigger
                  id="associated-chart-treatment-preference"
                  aria-label={t('treatmentPreference')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value={UserTreatmentPreference.Masculine}>
                    {t('treatmentPreferenceMasculine')}
                  </SelectItem>
                  <SelectItem value={UserTreatmentPreference.Feminine}>
                    {t('treatmentPreferenceFeminine')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* A third person's chart: the operator's own birthday is never offered. */}
              <BirthDateInput
                id="associated-chart-birth-date"
                required
                max={today}
                autoComplete="off"
                showErrors={showCreateBirthErrors}
                value={createForm.birthDate}
                onValueChange={(draft) => {
                  setCreateForm(current => ({ ...current, birthDate: draft }));
                }}
              />

              <TimeInput
                id="associated-chart-birth-time"
                required
                showErrors={showCreateBirthErrors}
                value={createForm.birthTime}
                onValueChange={(draft) => {
                  setCreateForm(current => ({ ...current, birthTime: draft }));
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="associated-chart-birth-place">{t('birthPlace')}</Label>
              <div className="space-y-2">
                <Input
                  id="associated-chart-birth-place"
                  placeholder={t('mapaAstralBirthPlacePlaceholder')}
                  value={createForm.birthPlace}
                  onChange={(event) => {
                    const value = event.target.value;
                    setCreateForm(current => ({
                      ...current,
                      birthPlace: value,
                      birthLatitude: undefined,
                      birthLongitude: undefined,
                    }));
                    setBirthPlaceQuery(value);
                  }}
                />

                {createForm.birthLatitude !== undefined && createForm.birthLongitude !== undefined && (
                  <div className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
                    <MapPin className="h-3.5 w-3.5" />
                    {t('mapaAstralCoordinates', {
                      lat: createForm.birthLatitude.toFixed(4),
                      lon: createForm.birthLongitude.toFixed(4),
                    })}
                  </div>
                )}

                {birthPlaceQuery.trim().length >= 3 && (
                  <div className="space-y-2">
                    {isSearchingBirthPlace && (
                      <p className="flex items-center gap-2 text-xs text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {t('mapaAstralSearchingLocations')}
                      </p>
                    )}

                    {birthPlaceError && (
                      <p className="text-xs text-red-500">{birthPlaceError}</p>
                    )}

                    {!isSearchingBirthPlace && !birthPlaceError && birthPlaceSuggestions.length > 0 && (
                      <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
                        {birthPlaceSuggestions.map(suggestion => (
                          <button
                            key={suggestion.id}
                            type="button"
                            className="flex w-full items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-action-50"
                            onClick={() => handleBirthPlaceSelect(suggestion)}
                          >
                            <span className="block text-slate-800">{suggestion.displayName}</span>
                            <span className="shrink-0 text-xs text-slate-500">
                              {suggestion.latitude.toFixed(2)}, {suggestion.longitude.toFixed(2)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-center gap-2 font-medium">
                <Sparkles className="h-4 w-4" />
                {t('mapaAstralAssociatedChartAiOnly')}
              </div>
              <p className="mt-1 text-xs text-amber-700">
                {t('mapaAstralAssociatedChartDescription')}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button variant="outline" className="bg-white" onClick={() => handleCreateDialogOpenChange(false)} disabled={isSavingChart}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreateAssociatedChart} disabled={!canCreateChart || isSavingChart || isAtChartLimit}>
              {isSavingChart && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('mapaAstralCreateChart')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('mapaAstralDeleteDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('mapaAstralDeleteDialogDescription', { name: selectedChart?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChart}>{t('goBack')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAssociatedChart} disabled={isDeletingChart}>
              {isDeletingChart && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('mapaAstralDeleteChart')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IonModal isOpen={showShare} onDidDismiss={closeShare}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('share')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeShare}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {shareUrl && <IonImg src={shareUrl} alt={t('imagePreviewAlt')} />}
          <IonText>
            <p>{t('sharePrivacyWarning')}</p>
          </IonText>
          <IonButton expand="block" onClick={handleDownload}>
            <IonIcon icon={downloadOutline} slot="start" />
            {t('download')}
          </IonButton>
          <IonButton expand="block" onClick={handleNativeShare} disabled={!navigator.share}>
            <IonIcon icon={shareOutline} slot="start" />
            {t('share')}
          </IonButton>
        </IonContent>
      </IonModal>
    </div>
  );
};

export default MapaAstral;
